import { calculateOriginalPrice } from '../../utils/priceComparison';
import AddProductButton from '../shared/AddProductButton';

const buildImageUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `https://${url}`;
};

/**
 * Tarjeta de producto para las filas horizontales del home (Populares y las
 * secciones que vengan después).
 *
 * Orden de arriba a abajo: foto → tiempo de preparación → precio → nombre →
 * botón Agregar. No reusa MenuProductCard porque esta va sobre tarjeta blanca
 * con botón ancho, y la del menú lleva franja y el "+" redondo.
 *
 * La foto llena por completo el cuadro redondeado: no hay fondo de relleno
 * debajo. Cualquier tinte que se vea ahí viene de la propia imagen, editada
 * así desde el dashboard — no lo pone el CSS.
 *
 * Props:
 *   - addButtonImage: imagen opcional del botón Agregar (config del home).
 */
export default function HomeProductCard({ product, addButtonImage, onClick }) {
  // Esta sección puede tener su propia foto, con otro encuadre que la del menú.
  const imagen = buildImageUrl(product.popularImage) || buildImageUrl(product.image);
  const original = calculateOriginalPrice(product.price, product._id || product.id);
  // tagId viene poblado desde la API; una etiqueta apagada no se pinta.
  const etiqueta = product.tagId?.isActive && product.tagId?.image ? product.tagId : null;

  // Div y no <button>: el botón Agregar trae sus propios botones dentro y
  // anidar botones es HTML inválido (el click del stepper se pierde).
  return (
    <div
      onClick={onClick}
      className="w-[8.25rem] flex-shrink-0 flex flex-col rounded-2xl bg-white p-1.5 pb-2 text-left cursor-pointer"
    >
      <div className="relative aspect-square rounded-xl overflow-hidden">
        {imagen ? (
          <img
            src={imagen}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder.svg'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
            Sin imagen
          </div>
        )}

        {/* Etiqueta montada sobre la foto, abajo a la derecha como en la
            referencia. Es una imagen subida desde el dashboard, así que aquí
            solo se posiciona: el arte y el texto son parte del archivo. */}
        {etiqueta && (
          <img
            src={buildImageUrl(etiqueta.image)}
            alt={etiqueta.name}
            className="absolute bottom-1 right-1 h-7 w-auto max-w-[80%] object-contain pointer-events-none"
            loading="lazy"
          />
        )}
      </div>

      <div className="px-1 pt-1.5">
        {product.prepTime > 0 && (
          <div className="flex items-center gap-1 text-homecard-time">
            {/* Reloj "medio lleno": el mismo glifo de la referencia. */}
            <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true" className="flex-shrink-0">
              <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1.4" />
              <path d="M6 1.6A4.4 4.4 0 0 1 6 10.4Z" fill="currentColor" />
            </svg>
            <span className="text-[11px] font-medium leading-none">{product.prepTime} mins</span>
          </div>
        )}

        {/* Precio en negro remarcado; el nombre debajo, con menos peso, para que
            no compitan.

            El precio NO usa font-display: Futura tiene cifras tabulares (toda
            cifra mide lo mismo), así que un "1" queda flotando con un hueco al
            lado y "180" se ve despegado. La Outfit del cuerpo es proporcional. */}
        <div className="flex items-baseline gap-1.5 mt-1">
          <span className="text-[15px] font-bold tracking-tight text-gray-900">
            ${product.price?.toFixed(0)}
          </span>
          {original && (
            <span className="text-[12px] font-normal text-gray-400 line-through">
              ${original}
            </span>
          )}
        </div>

        <h3 className="mt-0.5 text-[13px] font-medium text-gray-700 leading-snug line-clamp-2">
          {product.name}
        </h3>
      </div>

      {/* Márgenes chicos a propósito: la imagen del botón suele traer aire
          transparente alrededor, así que la caja tiene que ser más ancha que el
          botón que se ve. mt-auto lo manda al pie de la tarjeta: las tarjetas
          de una fila se estiran al mismo alto, así que todos los botones
          quedan al mismo nivel aunque el nombre ocupe uno o dos renglones. */}
      <div className="mx-0.5 mt-auto pt-2">
        <AddProductButton product={product} image={buildImageUrl(addButtonImage)} />
      </div>
    </div>
  );
}
